import {
  Badge,
  Box,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@elkaso-app/web-design';
import { useDocumentPreview } from 'apis/document-vault/use-document-preview';
import { useGetDocumentVersionsApi } from 'apis/document-vault/use-get-document-versions-api';
import React from 'react';
import { FiEye } from 'react-icons/fi';

interface DocumentVersionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  documentId: string;
  documentTypeName: string;
}

export const DocumentVersionsModal: React.FC<DocumentVersionsModalProps> = ({
  isOpen,
  onClose,
  restaurantId,
  documentId,
  documentTypeName,
}) => {
  const { data, isLoading } = useGetDocumentVersionsApi(restaurantId, documentId, {
    enabled: isOpen && !!documentId,
  });
  const versions = data?.data?.rows ?? data?.rows ?? [];
  const { previewVersion, loadingId } = useDocumentPreview();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='4xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Version History — {documentTypeName}
          <Text fontSize='sm' fontWeight='normal' color='gray.500'>
            All uploaded versions for this document
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isLoading ? (
            <Text color='gray.500' textAlign='center' py='lg'>
              Loading...
            </Text>
          ) : versions.length === 0 ? (
            <Text color='gray.500' textAlign='center' py='lg'>
              No versions found
            </Text>
          ) : (
            <Box overflowX='auto'>
              <Table size='sm'>
                <Thead>
                  <Tr>
                    <Th>Version</Th>
                    <Th>File Name</Th>
                    <Th>Upload Date</Th>
                    <Th>Uploaded By</Th>
                    <Th>Expiry Date</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {versions.map((version: any) => (
                    <Tr key={version.id}>
                      <Td>
                        <Badge colorScheme='blue'>v{version.versionNumber}</Badge>
                      </Td>
                      <Td>
                        <Text fontSize='sm'>{version.originalFileName}</Text>
                      </Td>
                      <Td>
                        <Text fontSize='sm' color='gray.600'>
                          {version.uploadedAt ? new Date(version.uploadedAt).toLocaleDateString() : '—'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize='sm' color='gray.600'>
                          {version.uploadedBy?.name ?? '—'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize='sm' color={version.expiryDate ? 'gray.800' : 'gray.400'}>
                          {version.expiryDate ?? '—'}
                        </Text>
                      </Td>
                      <Td>
                        <Tooltip label='Preview' placement='top'>
                          <IconButton
                            aria-label='Preview version'
                            icon={<FiEye />}
                            size='sm'
                            variant='ghost'
                            colorScheme='green'
                            isLoading={loadingId === `version-${version.id}`}
                            onClick={() =>
                              previewVersion(restaurantId, documentId, String(version.id))
                            }
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
